import { RfxProject } from "../../types/rfx.types";
import { RFX_ANSWER_CHOICE_TYPE, RFX_QUESTION_TYPE } from "../constants";

export type RfxAnswerChoiceOfType =
	(typeof RFX_ANSWER_CHOICE_TYPE)[keyof typeof RFX_ANSWER_CHOICE_TYPE];

export type RfxAnswerType =
	(typeof RFX_QUESTION_TYPE)[keyof typeof RFX_QUESTION_TYPE];

// Allowed document category format
export interface RfxDocumentCategory {
	category_id: string;
	subcategory_id?: string;
}

// --- Answer Types ---

export class ReturnableScheduleFileUpload {
	fileName: string;
	filePath: string;
	fileSize: number;

	constructor(
		file_name: string,
		file_path: string,
		file_size: string | number
	) {
		this.fileName = file_name;
		this.filePath = file_path;
		this.fileSize =
			typeof file_size === "number" ? file_size : parseInt(file_size);
	}
}

export interface AnswerWithChosenType {
	chosen_type: RfxAnswerChoiceOfType;
}

export interface CheckboxAnswer {
	type: typeof RFX_QUESTION_TYPE.CHECKBOX;
	value: number[];
}

export interface TableAnswer {
	type: typeof RFX_QUESTION_TYPE.TABLE;
	value: string[];
}

export type TableRowAnswer = [[{ id: string; value: string }]];

export interface TableRowsAnswer {
	type: typeof RFX_QUESTION_TYPE.TABLE_ROWS;
	value: TableRowAnswer;
}

export interface ProjectExperienceAnswer extends AnswerWithChosenType {
	type: typeof RFX_QUESTION_TYPE.PROJECT_EXPERIENCE;
	value: number[];
	selectedProjects?: RfxProject[];
	chosen_type: typeof RFX_ANSWER_CHOICE_TYPE.SELECT;
}

export interface KeyPersonnelAnswer extends AnswerWithChosenType {
	type: typeof RFX_QUESTION_TYPE.KEY_PERSONNEL;
	value: number[];
	selectedKeyPersonnel?: any[];
	chosen_type: typeof RFX_ANSWER_CHOICE_TYPE.SELECT;
}

export interface TextAnswer {
	type: typeof RFX_QUESTION_TYPE.TEXT;
	value: string;
}

export interface TextareaAnswer {
	type: typeof RFX_QUESTION_TYPE.TEXTAREA;
	value: string;
}

export interface RadioAnswer {
	type: typeof RFX_QUESTION_TYPE.RADIO;
	value: number;
}

export interface MultiSelectAnswer {
	type: typeof RFX_QUESTION_TYPE.SELECT;
	value: number[];
}

export interface DateAnswer {
	type: typeof RFX_QUESTION_TYPE.DATE;
	value: string;
}

export interface CorporateDocumentsAnswer extends AnswerWithChosenType {
	type: typeof RFX_QUESTION_TYPE.CORPORATE_DOCUMENTS;
	allow_document_categories: RfxDocumentCategory[];
	value: (number | ReturnableScheduleFileUpload)[];
	chosen_type:
		| typeof RFX_ANSWER_CHOICE_TYPE.SELECT
		| typeof RFX_ANSWER_CHOICE_TYPE.OTHER;
}

export interface CorporateDocumentsSelectAnswer
	extends CorporateDocumentsAnswer {
	value: number[];
	chosen_type: typeof RFX_ANSWER_CHOICE_TYPE.SELECT;
}

export interface CorporateDocumentsOtherAnswer
	extends CorporateDocumentsAnswer {
	value: ReturnableScheduleFileUpload[];
	chosen_type: typeof RFX_ANSWER_CHOICE_TYPE.OTHER;
}

export interface FileAnswer extends AnswerWithChosenType {
	type: typeof RFX_QUESTION_TYPE.FILE;
	ext: string[];
	value: ReturnableScheduleFileUpload[];
	chosen_type: typeof RFX_ANSWER_CHOICE_TYPE.OTHER;
}

export type CorporateDocumentResponses =
	| CorporateDocumentsOtherAnswer
	| CorporateDocumentsSelectAnswer;

// --- Union of all possible answers ---
export type RfxAnswerResponse =
	| CheckboxAnswer
	| TableAnswer
	| TableRowsAnswer
	| ProjectExperienceAnswer
	| KeyPersonnelAnswer
	| TextAnswer
	| TextareaAnswer
	| RadioAnswer
	| MultiSelectAnswer
	| DateAnswer
	| CorporateDocumentsAnswer
	| FileAnswer;

// --- Main RfxAnswer structure ---
export interface RfxAnswer {
	id: number;
	uuid: string | null;
	rfqId: number | null;
	rfqQuestionId: number | null;
	companyId: number | null;
	guestUserId: number | null;
	answer: RfxAnswerResponse;
	comment: string;
}

export type RfxAnswers = RfxAnswer[];
